import { createContext, useContext, useState, useEffect, useCallback } from "react";

const ThemeContext = createContext(null);

const THEMES = [
  { 
    id: "pink", 
    name: "Pink", 
    description: "Soft blush pink",
    color: "#F9A8D4"
  },
  { 
    id: "lavender", 
    name: "Lavender", 
    description: "Calm violet",
    color: "#C4B5FD"
  },
  { 
    id: "dark-navy", 
    name: "Dark Navy", 
    description: "Premium dark",
    color: "#1e293b"
  }
];

const DEFAULT_THEME = "pink";

// Default values to prevent null errors
const defaultContextValue = {
  theme: DEFAULT_THEME,
  setTheme: () => {},
  themes: THEMES
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  // Return default values if context is null (not wrapped in provider)
  return context || defaultContextValue;
};

const applyTheme = (themeId) => {
  const root = document.documentElement;
  
  // For pink (default), remove data-theme attribute
  // For others, set the data-theme attribute
  if (themeId === 'pink' || !themeId) {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', themeId);
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem("labstack-theme");
      return saved && THEMES.find(t => t.id === saved) ? saved : DEFAULT_THEME;
    }
    return DEFAULT_THEME;
  });

  // Apply theme on mount and when theme changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((newTheme) => {
    if (THEMES.find(t => t.id === newTheme)) {
      localStorage.setItem("labstack-theme", newTheme);
      setThemeState(newTheme);
      applyTheme(newTheme);
    }
  }, []);

  const value = {
    theme: theme || DEFAULT_THEME,
    setTheme,
    themes: THEMES
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
